import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, Tooltip, Dialog, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PlayArrow, Pause, Fullscreen, FullscreenExit, Refresh } from '@material-ui/icons';
import { getChannelThumbnail } from '../../utilities/mezzanineApi';

// The API returns a 640x360 PNG inline as base64, which measures ~900 KB per
// frame on the wire. At this interval that is roughly 230 KB/s for as long as a
// panel stays open, so the number is deliberately easy to find and change. It
// would drop by an order of magnitude if the backend returned JPEG.
const REFRESH_SECONDS = 4;

// Source is 16:9.
const WIDTH = 240;
const HEIGHT = 135;

const useStyles = makeStyles(() => ({
  frame: {
    position: 'relative',
    width: WIDTH,
    height: HEIGHT,
    backgroundColor: '#000',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    color: '#777',
    fontSize: '10px',
    textAlign: 'center',
    padding: '0 8px',
  },
  // Sits on the image rather than below it, so the thumbnail keeps its exact
  // 16:9 footprint and the controls do not shift the panel layout.
  controls: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '1px 2px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))',
  },
  controlButton: {
    padding: '3px',
    color: 'white',
  },
  controlIcon: {
    fontSize: '15px',
  },
  caption: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '9px',
    fontFamily: 'monospace',
    paddingLeft: '2px',
  },
  spinner: {
    position: 'absolute',
    top: '4px',
    right: '4px',
  },
  enlargedFrame: {
    position: 'relative',
    backgroundColor: '#000',
    lineHeight: 0,
  },
  enlargedImage: {
    display: 'block',
    maxWidth: '90vw',
    maxHeight: '85vh',
    backgroundColor: '#000',
  },
  // The enlarged view keeps polling, so it gets a taller bar with readable
  // controls rather than the 15px ones that suit a 240px-wide thumbnail.
  controlsLarge: {
    padding: '4px 6px',
  },
  controlIconLarge: {
    fontSize: '20px',
  },
  captionLarge: {
    fontSize: '11px',
  },
}));

// Rendered over both the small thumbnail and the enlarged view, so pause,
// refresh and the capture time stay available in either. `trailing` is the only
// difference: enlarge in one, exit in the other.
const ControlBar = ({
  classes,
  large,
  playing,
  onTogglePlay,
  onRefresh,
  loading,
  caption,
  trailing,
}) => {
  const iconClass = large ? classes.controlIconLarge : classes.controlIcon;

  return (
    <Box className={`${classes.controls} ${large ? classes.controlsLarge : ''}`}>
      <Tooltip title={playing ? `Pause (refreshing every ${REFRESH_SECONDS}s)` : 'Resume'}>
        <IconButton size="small" className={classes.controlButton} onClick={onTogglePlay}>
          {playing ? <Pause className={iconClass} /> : <PlayArrow className={iconClass} />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Refresh now">
        <IconButton
          size="small"
          className={classes.controlButton}
          onClick={onRefresh}
          disabled={loading}
        >
          <Refresh className={iconClass} />
        </IconButton>
      </Tooltip>

      <span className={`${classes.caption} ${large ? classes.captionLarge : ''}`}>
        {caption}
      </span>

      {trailing}
    </Box>
  );
};

const ChannelThumbnail = ({ channelId, signalPresent }) => {
  const classes = useStyles();
  const [frame, setFrame] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [enlarged, setEnlarged] = useState(false);

  // Guards against a slow response overlapping the next tick, and against
  // setting state after the channel changed or the panel closed.
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchFrame = useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setLoading(true);

    try {
      const response = await getChannelThumbnail(channelId);
      if (!mounted.current) {
        return;
      }
      if (!response.image_base64) {
        throw new Error('No image returned');
      }
      setFrame({
        src: `data:${response.content_type || 'image/png'};base64,${response.image_base64}`,
        slot: response.slot,
        sdi: response.sdi,
        at: new Date(),
      });
      setError(null);
    } catch (err) {
      if (mounted.current) {
        // Placeholder on failure rather than a stale frame, so the panel never
        // implies the input is fine when the capture is actually failing.
        setFrame(null);
        setError(err.message || 'Thumbnail unavailable');
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [channelId]);

  // Drop any previous channel's frame the moment the selection changes.
  useEffect(() => {
    setFrame(null);
    setError(null);
  }, [channelId]);

  useEffect(() => {
    // No signal means no picture to capture, so the backend is never called.
    if (!signalPresent || !playing) {
      return undefined;
    }

    let interval = null;

    const start = () => {
      if (interval === null) {
        fetchFrame();
        interval = setInterval(fetchFrame, REFRESH_SECONDS * 1000);
      }
    };

    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    // Polling a ~900 KB image into a tab nobody is looking at is pure waste.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') {
      start();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [channelId, signalPresent, playing, fetchFrame]);

  const caption = frame
    ? `slot ${frame.slot ?? '-'} · ${frame.sdi || '-'} · ${frame.at.toLocaleTimeString()}`
    : '';

  return (
    <>
      <Box className={classes.frame}>
        {!signalPresent ? (
          <Box className={classes.placeholder}>
            <span style={{ fontSize: '16px', lineHeight: 1 }}>—</span>
            <span>Input not locked</span>
          </Box>
        ) : frame ? (
          <img className={classes.image} src={frame.src} alt="Channel input thumbnail" />
        ) : (
          <Box className={classes.placeholder}>
            {loading ? (
              <CircularProgress size={18} style={{ color: '#555' }} />
            ) : (
              <span>{error || 'No thumbnail'}</span>
            )}
          </Box>
        )}

        {signalPresent && frame && loading && (
          <CircularProgress size={14} className={classes.spinner} style={{ color: 'rgba(255,255,255,0.8)' }} />
        )}

        {signalPresent && (
          <ControlBar
            classes={classes}
            playing={playing}
            onTogglePlay={() => setPlaying((v) => !v)}
            onRefresh={fetchFrame}
            loading={loading}
            caption={caption}
            trailing={
              frame && (
                <Tooltip title="Enlarge">
                  <IconButton
                    size="small"
                    className={classes.controlButton}
                    onClick={() => setEnlarged(true)}
                  >
                    <Fullscreen className={classes.controlIcon} />
                  </IconButton>
                </Tooltip>
              )
            }
          />
        )}
      </Box>

      <Dialog open={enlarged} onClose={() => setEnlarged(false)} maxWidth={false}>
        <Box className={classes.enlargedFrame}>
          {frame ? (
            <img className={classes.enlargedImage} src={frame.src} alt="Channel input thumbnail" />
          ) : (
            // Keeps the dialog open if a refresh fails while enlarged, rather
            // than collapsing to nothing.
            <Box
              className={classes.placeholder}
              style={{ width: '60vw', height: '33.75vw' }}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: '#555' }} />
              ) : (
                <span>{error || 'No thumbnail'}</span>
              )}
            </Box>
          )}

          <ControlBar
            classes={classes}
            large
            playing={playing}
            onTogglePlay={() => setPlaying((v) => !v)}
            onRefresh={fetchFrame}
            loading={loading}
            caption={`${channelId} · ${caption}`}
            trailing={
              <Tooltip title="Exit">
                <IconButton
                  size="small"
                  className={classes.controlButton}
                  onClick={() => setEnlarged(false)}
                >
                  <FullscreenExit className={classes.controlIconLarge} />
                </IconButton>
              </Tooltip>
            }
          />
        </Box>
      </Dialog>
    </>
  );
};

export default ChannelThumbnail;
